
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import Dropdown from "@/components/Reusable/Dropdown/Dropdown.vue";
import {
  getConditionsThatApplyToTemplateTypes,
  removeEmptyRulesFromRuleGroup
} from "@/lib/conditions/conditions";
import { RuleGroup } from "@/types/conditions";
import defensiveCopy from "@/util/defensive-copy";
import { configStore } from "@/store/store-accessor";
import ConditionsMatrix from "@/components/Reusable/CustomInputs/Conditions/ConditionsMatrix.vue";
import DropdownInput from "@/components/Reusable/CustomInputs/DropdownInput.vue";
import { WpMetaQuery, WpQuery } from "@/types/wp-query";
import ToggleInput from "@/components/Reusable/CustomInputs/ToggleInput.vue";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";
import NotificationLogger from "@/lib/notifications/notifications";
import { makeWordPressAjaxRequest } from "@/lib/wordpress/ajax";
import { PostTypesV } from "@/types/post-types";
import MultiselectInput from "@/components/Reusable/CustomInputs/MultiselectInput.vue";
import ButtonBarInput from "@/components/Reusable/CustomInputs/ButtonBarInput/ButtonBarInput.vue";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import CodeInput from "@/components/Reusable/CustomInputs/CodeInput/CodeInput.vue";
import Icon from "@/components/UI/Icon.vue";
import NumberInput from "@/components/Reusable/CustomInputs/NumberInput.vue";
import NiceButton from "@/components/UI/NiceButton.vue";
import Dialog from "@/components/UI/Dialog.vue";
import DatePickerInput from "@/components/Reusable/CustomInputs/DatePickerInput.vue";
import lodash from "lodash";
import WpMetaQueryComponent from "@/components/Reusable/CustomInputs/WpQuery/WpMetaQueryComponent.vue";

@Component({
  components: {
    WpMetaQueryComponent,
    DatePickerInput,
    Dialog,
    NiceButton,
    NumberInput,
    Icon,
    CodeInput,
    TextInput,
    ButtonBarInput,
    MultiselectInput,
    ToggleInput,
    DropdownInput,
    ConditionsMatrix,
    Dropdown
  }
})
export default class WpQueryControl extends Vue {
  @Prop()
  value!: WpQuery | null;

  open = false;
  customInternalValue = defensiveCopy(this.valueOrDefaults.custom);

  @Prop()
  options?: { postTypes: string[] };

  async mounted() {
    if (!configStore.postTypes.length) {
      pipe(
        await makeWordPressAjaxRequest({
          action: "breakdance_get_post_types_for_wp_query",
          decoder: PostTypesV,
          resultKey: "postTypes"
        }),
        fold(
          () => {
            NotificationLogger.log.message(
              "Failed to load necessary post types for the Wp Query control"
            );
          },
          postTypes => {
            configStore.setPostTypes(postTypes);
          }
        )
      );
    }
  }

  @Watch("value")
  emitDefaultsAfterClearingControl() {
    if (this.value?.active === "") {
      this.$emit("input", null);
      return;
    }

    if (!this.value) {
      this.customInternalValue = defensiveCopy(this.valueOrDefaults.custom);
    }
  }

  get valueOrDefaults(): WpQuery {
    const defaultPostType = this.options?.postTypes.length
      ? this.options?.postTypes[0]
      : "post";

    return (
      this.value || {
        active: "custom",
        text: `post_type=${defaultPostType}`,
        custom: {
          source: "post_types",
          // same as in "getWpQueryArgumentsFromWpQueryControlProperties" (PHP), keep it synced
          postsPerPage: 8,
          conditions: [[{}]],
          totalPosts: null,
          ignoreStickyPosts: false,
          ignoreCurrentPost: false,
          metaQuery: undefined,
          //*** These are all WP_query defaults
          postTypes: this.options?.postTypes || ["post"],
          orderBy: "date",
          order: "DESC",
          date: "all",
          beforeDate: null,
          afterDate: null,
          offset: null,
          acfField: null,
          metaboxField: null
          //****
        },
        php: `return ['post_type' => '${defaultPostType}'];`
      }
    );
  }

  updateTotalPosts(newValue: number | null) {
    if (typeof newValue !== "number") {
      this.customInternalValue.totalPosts = null;
      return;
    }

    // it doesn't make sense for "total posts" to be smaller than "posts per page".
    this.customInternalValue.totalPosts =
      newValue >= (this.customInternalValue.postsPerPage || 0)
        ? newValue
        : this.customInternalValue.postsPerPage;
  }

  updatePostsPerPage(newValue: number | null) {
    this.customInternalValue.postsPerPage = newValue;

    // it doesn't make sense for "total posts" to be smaller than "posts per page".
    if (
      newValue &&
      this.customInternalValue.totalPosts &&
      newValue >= this.customInternalValue.totalPosts
    ) {
      this.customInternalValue.totalPosts = newValue;
    }
  }

  closeDialog() {
    this.customInternalValue = defensiveCopy(this.valueOrDefaults.custom);

    this.open = false;
  }

  saveCustomChanges() {
    this.open = false;

    if (
      this.customInternalValue.source === "post_types" &&
      this.customInternalValue.conditions
    ) {
      const nonEmptyRules = removeEmptyRulesFromRuleGroup(
        this.customInternalValue.conditions
      );

      this.customInternalValue.conditions = nonEmptyRules.length
        ? nonEmptyRules
        : [[{}]];
    }

    const newValue = defensiveCopy(this.valueOrDefaults);
    newValue.custom = this.customInternalValue;

    this.$emit("input", newValue);
  }

  updateTextValue(newTextValue: WpQuery["text"] | null) {
    const newValue = defensiveCopy(this.valueOrDefaults);
    newValue.text = newTextValue ?? "";

    this.$emit("input", newValue);
  }

  updatePhpValue(nextPhpValue: WpQuery["php"] | null) {
    const newValue = defensiveCopy(this.valueOrDefaults);
    newValue.php = nextPhpValue ?? "";

    this.$emit("input", newValue);
  }

  updateActive(newActive: WpQuery["active"]) {
    const newValue = defensiveCopy(this.valueOrDefaults);
    newValue.active = newActive;

    this.$emit("input", newValue);
  }

  updateConditions(newValue: RuleGroup[]) {
    if (this.customInternalValue.source !== "post_types") return;

    // Make it undefined instead of saving an empty object, which can be turned
    // into an array in PHP and cause errors: https://github.com/soflyy/breakdance/issues/4020

    Vue.set(
      this.customInternalValue,
      "conditions",
      newValue.length ? newValue : undefined
    );
  }

  updateMetaQuery(metaQuery: WpMetaQuery) {
    if (this.customInternalValue.source !== "post_types") return;

    Vue.set(this.customInternalValue, "metaQuery", metaQuery);
  }

  get shouldShowStickyPostsToggle() {
    if (this.customInternalValue.source !== "post_types") return false;

    // Sticky posts only exist in "post" types, not in CPTs
    // https://core.trac.wordpress.org/ticket/12702
    return this.customInternalValue.postTypes.includes("post");
  }

  updatePostTypes(postTypes: string[]) {
    if (this.customInternalValue.source !== "post_types") return;

    this.customInternalValue.postTypes = postTypes;

    this.removeUnusableConditions(postTypes);
  }

  /**
   * If a user selects "pages" post type, then adds some posts to it, and removes the "pages" from post types
   * We want to remove that condition since it's no longer applicable
   */
  removeUnusableConditions(postTypes: string[]) {
    if (this.customInternalValue.source !== "post_types") return;
    if (
      !this.customInternalValue.conditions ||
      !this.customInternalValue.conditions.length
    )
      return;

    const postDropdownsPerPostType = postTypes.map(
      slug => `post-dropdown-${slug}`
    );

    // we only allow 1 condition array, hence [0]
    const conditionsWithoutPostTypeDropdownsThatArentSelected =
      this.customInternalValue.conditions[0]?.filter(
        condition =>
          condition.ruleSlug &&
          (!condition.ruleSlug.startsWith("post-dropdown-") ||
            postDropdownsPerPostType.includes(condition.ruleSlug))
      );

    this.customInternalValue.conditions = [
      conditionsWithoutPostTypeDropdownsThatArentSelected.length
        ? conditionsWithoutPostTypeDropdownsThatArentSelected
        : [{}]
    ];
  }

  get postTypesItems() {
    return configStore.postTypes
      .map(postType => ({
        text: postType.name,
        value: postType.slug
      }))
      .filter(postType => {
        if (!this.options?.postTypes) return true;

        return this.options?.postTypes.includes(postType.value);
      });
  }

  get hasUnsavedChanges() {
    return !lodash.isEqual(
      this.customInternalValue,
      this.valueOrDefaults.custom
    );
  }

  get sourceItems() {
    const sources = [
      { text: "Post Types", value: "post_types" },
      { text: "Related", value: "related" }
    ];

    if (configStore.availableWordpressPlugins.includes("ACF")) {
      sources.push({ text: "ACF Relationship", value: "acf_relationship" });
    }
    return sources;
  }

  get applicableConditions() {
    if (this.customInternalValue.source !== "post_types") return [];

    const selectedPostType = defensiveCopy(this.customInternalValue.postTypes);

    selectedPostType.push("ALL");

    const conditionsToInclude = configStore.conditions.filter(
      condition =>
        condition.availableForType.some(conditionPostType =>
          selectedPostType.includes(conditionPostType)
        ) && condition.supports.includes("query_builder")
    );

    return getConditionsThatApplyToTemplateTypes(
      conditionsToInclude,
      this.customInternalValue.postTypes
    );
  }

  // https://developer.wordpress.org/reference/classes/wp_query/#order-orderby-parameters
  get orderBy() {
    const orderByOptions = [
      {
        text: "Date",
        value: "date"
      },
      {
        text: "Last modified date",
        value: "modified"
      },
      {
        text: "Title",
        value: "title"
      },
      {
        text: "Comments count",
        value: "comment_count"
      },
      {
        text: "Menu order",
        value: "menu_order"
      },
      {
        text: "Random",
        value: "rand"
      }
    ];

    if (configStore.availableWordpressPlugins.includes("ACF")) {
      orderByOptions.push({
        text: "ACF",
        value: "acf_field"
      });
    }

    if (configStore.availableWordpressPlugins.includes("MetaBox")) {
      orderByOptions.push({
        text: "Meta Box",
        value: "metabox_field"
      });
    }

    return orderByOptions;
  }

  get order() {
    return [
      {
        text: "Descending",
        value: "DESC"
      },
      {
        text: "Ascending",
        value: "ASC"
      }
    ];
  }

  get dates() {
    return [
      {
        text: "All",
        value: "all"
      },
      // note that the date values are meant for PHP's "strtotime"
      {
        text: "Past day",
        value: "-1 day"
      },
      {
        text: "Past week",
        value: "-1 week"
      },
      {
        text: "Past Month",
        value: "-1 month"
      },
      {
        text: "Past Three month",
        value: "-3 months"
      },
      {
        text: "Past Year",
        value: "-1 year"
      },
      {
        text: "Custom",
        value: "custom"
      }
    ];
  }
}
